import { create } from 'zustand';
import type { PrefabDefinition, PrefabConfigValues, PrefabCategory } from '../types/prefab';
import { apiPrefabToDefinition } from '../types/prefab';
import { rivellumApi } from '../api/rivellumApi';

interface PrefabState {
  catalog: PrefabDefinition[];
  loading: boolean;
  error: string | null;
  searchQuery: string;
  selectedCategory: PrefabCategory | null;
  selectedPrefabId: string | null;
  configValues: PrefabConfigValues;
  configErrors: Record<string, string>;

  fetchCatalog: () => Promise<void>;
  setSearchQuery: (q: string) => void;
  setSelectedCategory: (cat: PrefabCategory | null) => void;
  selectPrefab: (id: string | null) => void;
  setConfigValue: (key: string, value: string) => void;
  setConfigErrors: (errors: Record<string, string>) => void;
  resetConfig: () => void;
  getFilteredCatalog: () => PrefabDefinition[];
  getSelectedPrefab: () => PrefabDefinition | null;
  validateConfig: () => Record<string, string>;
}

export const usePrefabStore = create<PrefabState>()((set, get) => ({
  catalog: [],
  loading: false,
  error: null,
  searchQuery: '',
  selectedCategory: null,
  selectedPrefabId: null,
  configValues: {},
  configErrors: {},

  fetchCatalog: async () => {
    if (get().catalog.length > 0 || get().loading) return;
    set({ loading: true, error: null });
    try {
      const res = await rivellumApi.getPrefabs();
      const defs = res.prefabs.map(apiPrefabToDefinition);
      set({ catalog: defs, loading: false });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to load prefabs', loading: false });
    }
  },

  setSearchQuery: (q) => set({ searchQuery: q }),

  setSelectedCategory: (cat) => set({ selectedCategory: cat }),

  selectPrefab: (id) => {
    if (!id) {
      set({ selectedPrefabId: null, configValues: {}, configErrors: {} });
      return;
    }
    const prefab = get().catalog.find(p => p.id === id);
    if (!prefab) return;

    const defaults: PrefabConfigValues = {};
    for (const param of prefab.params) {
      if (param.defaultValue !== undefined) {
        defaults[param.key] = String(param.defaultValue);
      } else if (param.type === 'boolean') {
        defaults[param.key] = 'false';
      }
    }
    set({ selectedPrefabId: id, configValues: defaults, configErrors: {} });
  },

  setConfigValue: (key, value) => {
    set(s => {
      const next = { ...s.configValues, [key]: value };
      const errors = { ...s.configErrors };
      delete errors[key];
      return { configValues: next, configErrors: errors };
    });
  },

  setConfigErrors: (errors) => set({ configErrors: errors }),

  resetConfig: () => set({ configValues: {}, configErrors: {} }),

  getFilteredCatalog: () => {
    const { catalog, searchQuery, selectedCategory } = get();
    let filtered = catalog;

    if (selectedCategory) {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q) ||
        p.tags.some(t => t.toLowerCase().includes(q)),
      );
    }

    return filtered;
  },

  getSelectedPrefab: () => {
    const { selectedPrefabId, catalog } = get();
    if (!selectedPrefabId) return null;
    return catalog.find(p => p.id === selectedPrefabId) ?? null;
  },

  validateConfig: () => {
    const { selectedPrefabId, configValues, catalog } = get();
    const prefab = catalog.find(p => p.id === selectedPrefabId);
    if (!prefab) return {};

    const errors: Record<string, string> = {};

    for (const param of prefab.params) {
      const value = configValues[param.key]?.trim() ?? '';

      if (param.required && !value) {
        errors[param.key] = `${param.label} is required`;
        continue;
      }

      if (!value) continue;

      if (param.type === 'number') {
        const num = Number(value);
        if (!Number.isFinite(num)) {
          errors[param.key] = `${param.label} must be a valid number`;
        } else if (param.minimum !== undefined && num < param.minimum) {
          errors[param.key] = `${param.label} must be at least ${param.minimum}`;
        } else if (param.maximum !== undefined && num > param.maximum) {
          errors[param.key] = `${param.label} must be at most ${param.maximum}`;
        }
      }

      if (param.type === 'address' && value) {
        if (!/^0x[0-9a-fA-F]{1,64}$/.test(value)) {
          errors[param.key] = `${param.label} must be a valid hex address (0x...)`;
        }
      }
    }

    set({ configErrors: errors });
    return errors;
  },
}));
